import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { defineColumns } from '@juvare/platform-shared/features/storage';
import { z } from 'zod';
import { badgeEntityName } from './BadgeEntityTypes';
export const accountBadgeFrontImageAttachmentKey = 'accountBadgeFrontImage';
export const accountBadgeBackImageAttachmentKey = 'accountBadgeBackImage';
export const accountBadgePrintedAttachmentKey = 'accountBadgePdf';
export const accountBadgeEntityName = 'accountBadge';
export const accountBadgeEntityIdSequence = 'accountBadgeEntityIdSequence';
export const accountBadgeStatusReasonSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    canReissue: z.boolean().optional(),
});
export const accountBadgeStatuses = ['valid', 'expired', 'revoked', 'reissueNeeded'];
export const accountBadgeDistributionStatuses = ['not-printed', 'processing', 'printed', 'distributed'];
export const accountBadgePrintStatuses = ['success', 'processing', 'failed'];
export const accountBadgeStatusSchema = z.enum(accountBadgeStatuses);
export const accountBadgeDistributionStatusSchema = z.enum(accountBadgeDistributionStatuses);
export const accountBadgePrintStatusSchema = z.enum(accountBadgePrintStatuses);
export const accountBadgeSchema = z.object({
    /**
     * Required to be set before creation:
     * `onTransform()` is async when executing inside `context.utils.executeInTransaction()`
     *
     * TODO consider removing after all the RMS Classic migration work is completed and it's proven that `accountBadgeNumber` alone is enough
     */
    accountBadgeNumericId: z.number(),
    /**
     * Final barcode badge number, if no barcode generation - uses plain `accountBadgeNumericId`
     *
     * Required to be set before creation
     */
    accountBadgeNumber: z.string(),
    badgeId: z.string().uuid(),
    accountId: z.string().uuid(),
    status: accountBadgeStatusSchema,
    statusReason: accountBadgeStatusReasonSchema.optional(),
    /**
     * this Account Badge was "replaced" (reissue) by other one
     */
    reissuedBy: z
        .object({
        accountBadgeId: z.string().uuid(),
        reissuedAt: z.number(),
    })
        .optional(),
    /**
     * this Account Badge is "replacing" (reissue) other one
     */
    reissueHistory: z
        .object({
        accountBadgeId: z.string().uuid(),
        reissuedAt: z.number(),
        fromStatus: accountBadgeStatusSchema,
    })
        .array()
        .optional(),
    issuedAt: z.number(),
    expiresAt: z.number(),
    statusLastUpdatedAt: z.number().optional(),
    distributionStatusLastUpdatedAt: z.number().optional(),
    distributionStatus: accountBadgeDistributionStatusSchema,
    /**
     * Account Badge entity options which are configurable during creation
     *
     * Hint: can extend an entity to support additional options fields
     */
    options: z
        .object({
        organizationId: z.string().uuid().optional(),
    })
        .passthrough(),
    /**
     * Template Data at the time of generation
     */
    templateDataSnapshot: z.any().optional(),
    /**
     * Print request is for internal use only.
     * used for print status tracking.
     */
    printRequest: z
        .object({
        status: accountBadgePrintStatusSchema,
        requestId: z.string().uuid().optional(),
        response: z.any().optional(),
    })
        .optional(),
    /**
     * Was Account badge migrated from legacy system
     */
    isMigrated: z.boolean().optional(),
});
export const accountBadgeColumns = defineColumns({
    /**
     * Numeric Badge ID - needed to generate `accountBadgeNumber`
     *
     * May be removed in future as `accountBadgeNumber` might prove to be enough
     */
    accountBadgeNumericId: {
        type: 'number-required',
        index: true,
    },
    /**
     * Badge Number - will be stored in barcode to identify specific accountBadge
     */
    accountBadgeNumber: {
        type: 'string-required',
        index: 'unique',
    },
    /**
     * Unique Index for `accountId` - ensures only one valid Account Badge exists for one Badge.
     *
     * Because a simple unique index is not enough, e.g., can have multiple invalid Account Badges for the same account.
     */
    uniqueAccountIdKey: {
        type: 'string',
        index: 'unique',
    },
    badgeId: {
        type: 'foreign-key',
        entity: badgeEntityName,
        onDeleteCascade: false,
    },
    badgeName: {
        type: 'foreign-column',
        foreignEntity: badgeEntityName,
        mappedByColumn: 'badgeId',
        foreignColumn: 'name',
        foreignColumnType: 'string',
    },
    accountId: {
        type: 'foreign-key',
        entity: accountEntityName,
        onDeleteCascade: false,
    },
    organizationId: {
        type: 'string',
    },
    issuedAt: {
        type: 'number',
    },
    status: {
        type: 'string-required',
    },
    isStatusValid: {
        type: 'boolean-required',
    },
    distributionStatus: {
        type: 'string-required',
    },
    expiresAt: {
        type: 'number-required',
    },
    statusLastUpdatedAt: {
        type: 'number',
    },
    distributionStatusLastUpdatedAt: {
        type: 'number',
    },
    statusReasonTitle: {
        type: 'string',
    },
    statusReasonDescription: {
        type: 'string',
    },
    statusReasonCanReissue: {
        type: 'boolean',
    },
    reissuedByAccountBadgeId: {
        type: 'string',
    },
    accountDisplayName: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'displayName',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    accountSystemRole: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'systemRole',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    previousAccountBadgeIds: {
        type: 'string-array',
        index: true,
    },
    printStatus: {
        type: 'string',
        index: true,
    },
    printRequestId: {
        type: 'string',
        index: true,
    },
    isMigrated: {
        type: 'boolean',
    },
});
